import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, BookOpen, FileText, Settings, LogOut, Menu, X, Plus, ShieldAlert,
} from "lucide-react";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/quotes", label: "Quotes", icon: FileText, testid: "nav-quotes" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/catalog", label: "Price Catalog", icon: BookOpen, testid: "nav-catalog" },
  { to: "/settings", label: "Business Profile", icon: Settings, testid: "nav-settings" },
];

export const AppShell = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const SideContent = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 border-b border-slate-800">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-blue-600 flex items-center justify-center text-white font-black text-sm tracking-tighter">
            WVT
          </div>
          <div>
            <p className="text-white font-bold leading-none" style={{ fontFamily: "Manrope, sans-serif" }}>
              QuoteFlow
            </p>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">AU / VIC Ready</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <n.icon size={18} strokeWidth={2} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <button
          data-testid="new-quote-sidebar-btn"
          onClick={() => { setOpen(false); navigate("/quotes/new"); }}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2.5 text-sm font-semibold transition-colors active:scale-95"
        >
          <Plus size={16} /> New Document
        </button>
        <div className="mt-4 flex items-center gap-3 px-2">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-slate-700" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-white truncate" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
          </div>
          <button data-testid="logout-btn" onClick={logout} aria-label="Log out"
            className="text-slate-500 hover:text-white transition-colors">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-60 bg-slate-900 flex-col">{SideContent}</aside>

      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between bg-slate-900 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-blue-600 flex items-center justify-center text-white font-black text-xs">WVT</div>
          <span className="text-white font-bold text-sm">QuoteFlow</span>
        </div>
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="text-white" aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>
      {open && (
        <div className="md:hidden fixed inset-0 z-50 bg-slate-900" data-testid="mobile-nav-panel">
          <div className="flex justify-end p-4">
            <button onClick={() => setOpen(false)} className="text-white" aria-label="Close menu"><X size={22} /></button>
          </div>
          {SideContent}
        </div>
      )}

      <main className="md:pl-60">
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export const ComplianceNote = () => (
  <div className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
    <ShieldAlert size={16} className="mt-0.5 shrink-0" />
    <p>
      General guidance for Australian (VIC) invoicing only — not legal or tax advice. Verify GST and ATO
      tax-invoice requirements with your accountant.
    </p>
  </div>
);
